import { useEffect, useState } from 'react'
import { motion } from 'motion/react'
import { clsx } from 'clsx'

interface ScoreGaugeProps {
  score: number
  size?: 'sm' | 'md' | 'lg'
  /** Vypnout animaci (např. v PDF/printu) */
  animate?: boolean
}

const SIZES = {
  sm: { width: 72, stroke: 6, font: 'text-lg' },
  md: { width: 120, stroke: 9, font: 'text-3xl' },
  lg: { width: 180, stroke: 12, font: 'text-5xl' },
}

export default function ScoreGauge({ score, size = 'md', animate = true }: ScoreGaugeProps) {
  const target = Math.max(0, Math.min(100, Math.round(score ?? 0)))
  const { width, stroke, font } = SIZES[size]
  const radius = (width - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (target / 100) * circumference

  const color = target >= 80 ? '#10b981' : target >= 50 ? '#f59e0b' : '#ef4444'
  const colorLight = target >= 80 ? '#34d399' : target >= 50 ? '#fbbf24' : '#f87171'
  const colorClass = target >= 80 ? 'text-emerald-500 dark:text-emerald-400'
    : target >= 50 ? 'text-amber-500 dark:text-amber-400' : 'text-red-500 dark:text-red-400'
  const glowColor = target >= 80 ? '16,185,129' : target >= 50 ? '245,158,11' : '239,68,68'

  // Odpočítání čísla 0 → target (easeOutCubic)
  const [display, setDisplay] = useState(animate ? 0 : target)
  useEffect(() => {
    if (!animate) { setDisplay(target); return }
    let raf = 0
    let start: number | null = null
    const dur = 1100
    const tick = (now: number) => {
      if (start === null) start = now
      const p = Math.min(1, (now - start) / dur)
      const eased = 1 - Math.pow(1 - p, 3)
      setDisplay(Math.round(target * eased))
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, animate])

  const gid = `gauge-${size}-${target}`

  return (
    <motion.div
      initial={animate ? { scale: 0.85, opacity: 0 } : false}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      className="relative inline-flex items-center justify-center"
      style={{ width, height: width }}
    >
      {/* Měkký glow */}
      <div className="absolute inset-1 rounded-full blur-xl opacity-25"
        style={{ background: `radial-gradient(circle, rgba(${glowColor},0.55), transparent 70%)` }} />

      <svg className="-rotate-90 relative z-10" width={width} height={width}>
        <defs>
          <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colorLight} />
            <stop offset="100%" stopColor={color} />
          </linearGradient>
        </defs>
        {/* Track */}
        <circle cx={width / 2} cy={width / 2} r={radius} fill="none" strokeWidth={stroke}
          stroke="currentColor" className="text-gray-100 dark:text-white/10" />
        {/* Progress arc — sweep od 0 */}
        <motion.circle
          cx={width / 2} cy={width / 2} r={radius} fill="none"
          stroke={`url(#${gid})`} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circumference}
          initial={animate ? { strokeDashoffset: circumference } : false}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          style={{ filter: `drop-shadow(0 0 ${stroke / 2}px rgba(${glowColor},0.4))` }}
        />
      </svg>

      {/* Číslo */}
      <div className="absolute z-10 text-center leading-none">
        <span className={clsx('font-extrabold tracking-tight tabular-nums', font, colorClass)}>{display}</span>
        {size === 'lg' && <p className="text-xs text-gray-400 font-medium mt-1">/ 100</p>}
      </div>
    </motion.div>
  )
}
